import React, { useEffect, useRef, useState, useCallback } from "react";
import { Toast } from "primereact/toast";
import MessagingIntegrationService from "../services/MessagingIntegrationService";
import {
  SaCard,
  SaPrimaryButton,
  SaFormActions,
  SaField,
  SaSectionTitle,
  saInputSx,
  SA,
} from "./shared/SuperAdminUi";

const defaults = (clientId) => ({
  id: 0,
  clientId,
  integrationEnabled: false,
  smsEnabled: false,
  whatsAppEnabled: false,
  twilioAccountSid: "",
  twilioAuthToken: "",
  smsFromNumber: "",
  whatsAppFromNumber: "",
});

function MessagingIntegration({ ClientId }) {
  const toast = useRef(null);
  const [form, setForm] = useState(defaults(ClientId));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!ClientId) return;
    setLoading(true);
    try {
      const row = await MessagingIntegrationService.getByClientId(ClientId);
      if (row && row.id) {
        setForm({
          id: row.id,
          clientId: ClientId,
          integrationEnabled: !!row.integrationEnabled,
          smsEnabled: !!row.smsEnabled,
          whatsAppEnabled: !!row.whatsAppEnabled,
          twilioAccountSid: row.twilioAccountSid ?? "",
          twilioAuthToken: "",
          smsFromNumber: row.smsFromNumber ?? "",
          whatsAppFromNumber: row.whatsAppFromNumber ?? "",
        });
      } else {
        setForm(defaults(ClientId));
      }
    } catch (e) {
      console.error(e);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Could not load messaging settings.",
        life: 4000,
      });
      setForm(defaults(ClientId));
    } finally {
      setLoading(false);
    }
  }, [ClientId]);

  useEffect(() => {
    load();
  }, [load]);

  const change = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    if (!ClientId) return;

    const payload = {
      id: form.id,
      clientId: Number(ClientId),
      integrationEnabled: form.integrationEnabled,
      smsEnabled: form.smsEnabled,
      whatsAppEnabled: form.whatsAppEnabled,
      twilioAccountSid: form.twilioAccountSid.trim(),
      twilioAuthToken: form.twilioAuthToken.trim(),
      smsFromNumber: form.smsFromNumber.trim(),
      whatsAppFromNumber: form.whatsAppFromNumber.trim(),
      isActive: true,
      createdBy: 0,
      createdOn: new Date().toISOString(),
      modifiedBy: 0,
      modifiedOn: new Date().toISOString(),
    };

    if (form.integrationEnabled) {
      if (!payload.twilioAccountSid) {
        toast.current?.show({ severity: "warn", summary: "Twilio Account SID required", life: 3500 });
        return;
      }
      if (!payload.twilioAuthToken && !form.id) {
        toast.current?.show({
          severity: "warn",
          summary: "Auth token required",
          detail: "Enter Twilio Auth Token to create this integration.",
          life: 4000,
        });
        return;
      }
      if (form.smsEnabled && !payload.smsFromNumber) {
        toast.current?.show({ severity: "warn", summary: "SMS From required", detail: "E.164 number for SMS sender.", life: 4000 });
        return;
      }
      if (form.whatsAppEnabled && !payload.whatsAppFromNumber) {
        toast.current?.show({
          severity: "warn",
          summary: "WhatsApp From required",
          detail: "Use whatsapp:+... format or digits only; we normalize with whatsapp: prefix.",
          life: 4500,
        });
        return;
      }
    }

    setSaving(true);
    try {
      if (form.id > 0) {
        await MessagingIntegrationService.update(payload);
        toast.current?.show({ severity: "success", summary: "Saved", detail: "Messaging settings updated.", life: 2800 });
      } else {
        await MessagingIntegrationService.create(payload);
        toast.current?.show({ severity: "success", summary: "Saved", detail: "Messaging integration created.", life: 2800 });
      }
      await load();
    } catch (err) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Could not save messaging settings.",
        life: 4000,
      });
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const grid = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
    gap: "0 18px",
  };

  const chk = (name, label, hint) => (
    <SaField label={label}>
      <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer", fontSize: "14px", color: SA.ink }}>
        <input type="checkbox" name={name} checked={!!form[name]} onChange={change} />
        {hint ? <span style={{ fontSize: "12px", color: SA.muted, fontWeight: "400" }}>{hint}</span> : null}
      </label>
    </SaField>
  );

  if (loading) {
    return (
      <SaCard>
        <p style={{ margin: 0, color: SA.muted }}>Loading messaging settings…</p>
      </SaCard>
    );
  }

  return (
    <div>
      <Toast ref={toast} />
      <form onSubmit={save}>
        <SaCard>
          <SaSectionTitle iconClass="fas fa-comment-dots">SMS & WhatsApp (Twilio)</SaSectionTitle>
          <p style={{ margin: "-8px 0 18px", fontSize: "13px", color: SA.muted, lineHeight: 1.55 }}>
            Super Admin enables outbound messaging per agency. When turned on, the API uses Twilio to send SMS or WhatsApp using the numbers below.
            Auth tokens are not shown after save; leave the token blank on update to keep the existing secret.
          </p>

          <div style={{ ...grid, marginBottom: "14px" }}>
            {chk("integrationEnabled", "Enable messaging integration", "Master switch for this client")}
            {chk("smsEnabled", "Allow SMS", null)}
            {chk("whatsAppEnabled", "Allow WhatsApp", null)}
          </div>

          <div style={grid}>
            <SaField label="Twilio Account SID">
              <input name="twilioAccountSid" value={form.twilioAccountSid} onChange={change} style={saInputSx} autoComplete="off" />
            </SaField>
            <SaField label={`Twilio Auth Token${form.id ? " (leave blank to keep current)" : ""}`}>
              <input
                name="twilioAuthToken"
                type="password"
                autoComplete="new-password"
                value={form.twilioAuthToken}
                onChange={change}
                style={saInputSx}
              />
            </SaField>
            <SaField label="SMS From (E.164)">
              <input name="smsFromNumber" placeholder="+15551234567" value={form.smsFromNumber} onChange={change} style={saInputSx} />
            </SaField>
            <SaField label="WhatsApp From">
              <input
                name="whatsAppFromNumber"
                placeholder="whatsapp:+14155238886"
                value={form.whatsAppFromNumber}
                onChange={change}
                style={saInputSx}
              />
            </SaField>
          </div>

          <SaFormActions>
            <SaPrimaryButton type="submit" disabled={saving} iconClass={saving ? "fas fa-spinner fa-spin" : "fas fa-save"}>
              {saving ? "Saving…" : form.id ? "Update messaging" : "Save messaging"}
            </SaPrimaryButton>
          </SaFormActions>
        </SaCard>
      </form>
    </div>
  );
}

export default MessagingIntegration;
